import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ChevronLeft, Phone, MessageCircle, Calendar, Users, UtensilsCrossed,
  Wine, Clock, MapPin, User, Download, Package as PackageIcon,
} from 'lucide-react';
import axios from 'axios';
import { drinkItems, eventTypes, partners, getPackageById } from '../data/mockData';
import { generateBudgetPdf } from '../utils/generatePdf';
import { formatDateBR } from '../components/InlineDatePicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_LABELS = {
  lead_capturado: { label: 'Capturado', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  orcamento_gerado: { label: 'Orçamento', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  lead_quente: { label: 'Lead Quente', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  nao_clicou: { label: 'Não Clicou', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
  lead_frio: { label: 'Lead Frio', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
  em_negociacao: { label: 'Negociação', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  fechado: { label: 'Fechado', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  perdido: { label: 'Perdido', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
};
const ALL_STATUSES = Object.keys(STATUS_LABELS);

const LeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/leads/${id}`);
        setLead(res.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetch();
  }, [id]);

  const updateStatus = async (newStatus) => {
    try {
      await axios.patch(`${API}/leads/${id}/status`, { status: newStatus });
      setLead((prev) => ({ ...prev, status: newStatus }));
    } catch (e) { console.error(e); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#8B6F47] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!lead) return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
      <p className="text-white/40 font-body">Lead não encontrado</p>
    </div>
  );

  const statusInfo = STATUS_LABELS[lead.status] || { label: lead.status, color: 'bg-white/5 text-white/50' };

  const downloadLeadPDF = () => {
    const pkg = lead.package_id ? getPackageById(lead.package_id) : null;
    // Resolve activeItems = items do pacote menos os excluded
    const excluded = new Set(lead.excluded_items || []);
    const activeItems = pkg ? pkg.items.filter((it) => !excluded.has(it.id)) : [];
    generateBudgetPdf({
      leadName: lead.name,
      leadWhatsapp: lead.whatsapp,
      serviceType: lead.service_type,
      eventType: lead.event_type,
      guests: lead.guests,
      eventDate: lead.event_date,
      eventLocation: lead.location_to_define ? 'A definir' : (lead.event_location || ''),
      selectedPackage: pkg,
      activeItems,
      substitutePratoQuente: !!lead.substitute_prato_quente,
      selectedDrinks: lead.drinks || [],
      selectedPartners: lead.partners || [],
      deliveryQuantities: lead.delivery_items || {},
      totalPrice: lead.total_price,
    });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] font-body">
      <header className="bg-[#111111] border-b border-white/5 px-4 sm:px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin')} className="text-white/40 hover:text-white transition-colors" data-testid="back-btn">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h1 className="font-display text-lg text-white">{lead.name}</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={downloadLeadPDF}
              data-testid="admin-download-pdf"
              className="flex items-center gap-2 border border-[#C9A961]/40 text-[#C9A961] hover:bg-[#C9A961]/8 hover:border-[#C9A961]/60 font-body text-xs font-semibold tracking-wider uppercase px-4 py-2 rounded-sm transition-all">
              <Download className="w-3.5 h-3.5" /> PDF
            </button>
            <a href={`https://wa.me/55${lead.whatsapp?.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 bg-gradient-to-r from-[#8B6F47] to-[#C9A961] text-[#1a1611] font-body text-xs font-semibold tracking-wider uppercase px-4 py-2 rounded-sm hover:shadow-lg hover:shadow-[#C9A961]/25 transition-all">
              <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
            </a>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="md:col-span-2 space-y-4">
            {/* Contact */}
            <div className="p-5 rounded-sm border border-white/5 bg-[#111111]">
              <h3 className="font-body text-[10px] uppercase tracking-wider text-white/30 mb-3">Contato</h3>
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-[#C9A961]" />
                  <span className="text-sm text-white">{lead.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[#C9A961]" />
                  <span className="text-sm text-white">{lead.whatsapp}</span>
                </div>
              </div>
            </div>

            {/* Event Details */}
            <div className="p-5 rounded-sm border border-white/5 bg-[#111111]">
              <h3 className="font-body text-[10px] uppercase tracking-wider text-white/30 mb-3">Evento</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div>
                  <span className="text-[10px] text-white/25 uppercase tracking-wider block">Tipo</span>
                  <span className="text-sm text-white capitalize">{lead.service_type}</span>
                </div>
                {lead.event_type && (
                  <div>
                    <span className="text-[10px] text-white/25 uppercase tracking-wider block">Evento</span>
                    <span className="text-sm text-white">{eventTypes.find(e => e.id === lead.event_type)?.label || lead.event_type}</span>
                  </div>
                )}
                {lead.guests && (
                  <div>
                    <span className="text-[10px] text-white/25 uppercase tracking-wider block">Convidados</span>
                    <span className="text-sm text-white">{lead.guests}</span>
                  </div>
                )}
                {lead.event_date && (
                  <div>
                    <span className="text-[10px] text-white/25 uppercase tracking-wider block">Data</span>
                    <span className="text-sm text-white">{formatDateBR(lead.event_date)}</span>
                  </div>
                )}
                <div className="sm:col-span-2">
                  <span className="text-[10px] text-white/25 uppercase tracking-wider block">Local</span>
                  <span className="text-sm text-white">
                    {lead.location_to_define ? <span className="italic text-white/55">A definir</span> : (lead.event_location || '—')}
                  </span>
                </div>
              </div>
            </div>

            {/* Package */}
            {lead.package_name && (() => {
              const pkg = lead.package_id ? getPackageById(lead.package_id) : null;
              const excluded = new Set(lead.excluded_items || []);
              const items = pkg ? pkg.items.filter((it) => !excluded.has(it.id)) : [];
              return (
                <div className="p-5 rounded-sm border border-white/5 bg-[#111111]">
                  <div className="flex items-center gap-2 mb-3">
                    <PackageIcon className="w-3.5 h-3.5 text-[#C9A961]" />
                    <h3 className="font-body text-[10px] uppercase tracking-wider text-white/30">Pacote escolhido</h3>
                    {lead.package_tier && (
                      <span className="ml-auto text-[9px] uppercase tracking-wider bg-[#C9A961]/15 border border-[#C9A961]/30 text-[#E6CC8A] px-2 py-0.5 rounded-full">{lead.package_tier}</span>
                    )}
                  </div>
                  <span className="font-display text-base text-white block mb-2">{lead.package_name}</span>
                  {items.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {items.map((it) => (
                        <span key={it.id} className="px-2.5 py-1 rounded-full bg-white/[0.04] border border-white/8 text-[11px] text-white/60">{it.label}</span>
                      ))}
                      {lead.substitute_prato_quente && (
                        <span className="px-2.5 py-1 rounded-full bg-[#C9A961]/15 border border-[#C9A961]/35 text-[11px] text-[#E6CC8A]">+ Almoço/Jantar</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Drinks */}
            {lead.drinks?.length > 0 && (
              <div className="p-5 rounded-sm border border-white/5 bg-[#111111]">
                <h3 className="font-body text-[10px] uppercase tracking-wider text-white/30 mb-3">Bebidas</h3>
                <div className="flex flex-wrap gap-1.5">
                  {lead.drinks.map((id) => {
                    const item = drinkItems.find(d => d.id === id);
                    return item ? (
                      <span key={id} className="px-2.5 py-1 rounded-full bg-white/[0.04] border border-white/8 text-[11px] text-white/60">{item.label} (R${item.pricePerPerson}/p)</span>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            {/* Partners */}
            {lead.partners?.length > 0 && (
              <div className="p-5 rounded-sm border border-white/5 bg-[#111111]">
                <h3 className="font-body text-[10px] uppercase tracking-wider text-white/30 mb-3">Interesse em Parceiros</h3>
                <div className="flex flex-wrap gap-1.5">
                  {lead.partners.map((id) => {
                    const p = partners.find(x => x.id === id);
                    return p ? (
                      <span key={id} className="px-2.5 py-1 rounded-full bg-[#8B6F47]/10 border border-[#8B6F47]/20 text-[11px] text-[#D4B483]">{p.label}</span>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            {/* Interactions */}
            {lead.interactions?.length > 0 && (
              <div className="p-5 rounded-sm border border-white/5 bg-[#111111]">
                <h3 className="font-body text-[10px] uppercase tracking-wider text-white/30 mb-3">Histórico</h3>
                <div className="space-y-2">
                  {lead.interactions.map((int, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#8B6F47] flex-shrink-0" />
                      <span className="text-xs text-white/50">{int.action.replace(/_/g, ' ')}</span>
                      <span className="text-[10px] text-white/20 ml-auto">{new Date(int.timestamp).toLocaleString('pt-BR')}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Lead Score */}
            {(() => {
              const score = lead.lead_score ?? 0;
              const tone = score >= 75 ? { label: 'Quente', color: 'text-orange-400', bar: 'from-orange-500 to-orange-300' } :
                           score >= 50 ? { label: 'Morno', color: 'text-[#C9A961]', bar: 'from-[#8B6F47] to-[#D4B483]' } :
                           score >= 25 ? { label: 'Frio', color: 'text-white/55', bar: 'from-white/20 to-white/40' } :
                                          { label: 'Inativo', color: 'text-white/30', bar: 'from-white/10 to-white/20' };
              return (
                <div data-testid="lead-score-card" className="p-5 rounded-sm border border-white/5 bg-[#111111]">
                  <h3 className="font-body text-[10px] uppercase tracking-wider text-white/30 mb-3">Lead Score</h3>
                  <div className="flex items-baseline justify-between mb-3">
                    <span className={`font-display text-3xl font-bold ${tone.color}`}>{score}</span>
                    <span className={`text-[11px] uppercase tracking-wider font-medium ${tone.color}`}>{tone.label}</span>
                  </div>
                  <div className="h-1.5 bg-white/[0.05] rounded-full overflow-hidden">
                    <div className={`h-full bg-gradient-to-r ${tone.bar} rounded-full transition-all duration-700`} style={{ width: `${score}%` }} />
                  </div>
                  <p className="text-[10px] text-white/30 mt-2 leading-snug">Calculado por valor, número de convidados, engajamento e parceiros.</p>
                </div>
              );
            })()}

            {/* Status */}
            <div className="p-5 rounded-sm border border-white/5 bg-[#111111]">
              <h3 className="font-body text-[10px] uppercase tracking-wider text-white/30 mb-3">Status</h3>
              <select value={lead.status} onChange={(e) => updateStatus(e.target.value)}
                className="w-full bg-[#0a0a0a] border border-white/10 rounded-sm px-3 py-2 font-body text-sm text-white focus:outline-none focus:border-[#8B6F47]/40 cursor-pointer mb-3">
                {ALL_STATUSES.map((s) => (
                  <option key={s} value={s} className="bg-[#111111]">{STATUS_LABELS[s]?.label || s}</option>
                ))}
              </select>
              <div className={`inline-flex px-3 py-1 rounded-full border text-[11px] ${statusInfo.color}`}>
                {statusInfo.label}
              </div>
            </div>

            {/* Price */}
            <div className="p-5 rounded-sm border border-[#8B6F47]/20 bg-[#8B6F47]/[0.04]">
              <h3 className="font-body text-[10px] uppercase tracking-wider text-white/30 mb-2">Valor Total</h3>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#C9A961] to-[#D4B483] font-display text-3xl font-bold">
                R${lead.total_price?.toLocaleString('pt-BR')}
              </span>
            </div>

            {/* Engagement */}
            <div className="p-5 rounded-sm border border-white/5 bg-[#111111]">
              <h3 className="font-body text-[10px] uppercase tracking-wider text-white/30 mb-3">Engajamento</h3>
              <div className="space-y-2 mb-3">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${lead.whatsapp_clicked ? 'bg-green-400' : 'bg-white/15'}`} />
                  <span className="text-xs text-white/55">WhatsApp {lead.whatsapp_clicked ? 'clicado' : 'não clicado'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${lead.pdf_downloaded ? 'bg-green-400' : 'bg-white/15'}`} />
                  <span className="text-xs text-white/55">PDF {lead.pdf_downloaded ? 'baixado' : 'não baixado'}</span>
                </div>
              </div>
              <a href={`https://wa.me/55${lead.whatsapp?.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#8B6F47] to-[#C9A961] text-[#1a1611] font-body text-xs font-semibold uppercase py-2.5 rounded-sm hover:shadow-lg hover:shadow-[#C9A961]/25 transition-all">
                <Phone className="w-3.5 h-3.5" /> Abrir WhatsApp
              </a>
            </div>

            {/* Created */}
            <div className="p-5 rounded-sm border border-white/5 bg-[#111111]">
              <h3 className="font-body text-[10px] uppercase tracking-wider text-white/30 mb-2">Criado em</h3>
              <span className="text-xs text-white/50">{lead.created_at ? new Date(lead.created_at).toLocaleString('pt-BR') : '—'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadDetail;
